import { LOOKUP_CONFIG } from "@/config/lookups";
import { STUB_PAGES } from "@/config/stubPages";
import { REPORTING_SUITES } from "@/config/reportingSuites";
import { CONFIG_NAMESPACES } from "@/config/configNamespaces";
import { MY_WORK_SOURCES } from "@/config/myWorkSources";

const setTitle = (title: string) => {
  return title
    ? `${title} | Demulla Accounting Portal`
    : "Demulla Accounting Portal";
};

const authRoutes = [
  {
    path: "/auth/login",
    name: "auth.login",
    meta: {
      title: setTitle("Login"),
    },
    component: () => import("@/views/auth/Login.vue"),
  },
  {
    path: "/auth/verify-sso",
    name: "auth.verify-sso",
    meta: {
      title: setTitle("SSO Sign-in"),
    },
    component: () => import("@/views/auth/verify-sso.vue"),
  },
  {
    path: "/auth/login-otp/:node",
    name: "auth.login-otp",
    meta: {
      title: setTitle("Login OTP"),
    },
    component: () => import("@/views/auth/LoginOtp.vue"),
  },
  {
    path: "/auth/authenticator/:node/:enabled",
    name: "auth.authenticator",
    meta: {
      title: setTitle("Authenticator OTP"),
    },
    component: () => import("@/views/auth/Authenticator.vue"),
  },
  {
    path: "/auth/reset-password",
    name: "auth.reset",
    meta: {
      title: setTitle("Reset Password"),
    },
    component: () => import("@/views/auth/ResetPass.vue"),
  },
];

const errorRoutes = [
  {
    path: "/auth/error-404",
    name: "error.404",
    meta: {
      title: setTitle("Error 404"),
    },
    component: () => import("@/views/auth/Error404.vue"),
  },
  {
    path: "/auth/error-500",
    name: "error.500",
    meta: {
      title: setTitle("Error 500"),
    },
    component: () => import("@/views/auth/Error500.vue"),
  },
  {
    path: "/auth/error-403",
    name: "error.403",
    meta: {
      title: setTitle("Error 403"),
    },
    component: () => import("@/views/auth/Error403.vue"),
  },
  {
    path: "/:catchAll(.*)",
    redirect: "/auth/error-404",
  },
];

const dashboardRoutes = [
  {
    path: "/",
    name: "dashboard.index",
    meta: {
      title: setTitle("Dashboard"),
      authRequired: true,
      permission: "Can view Dashboard",
    },
    component: () => import("@/views/dashboard/DashboardPage.vue"),
  },
  {
    path: "/:id",
    name: "profile.view",
    meta: {
      title: setTitle("Dashboard"),
      authRequired: true,
    },
    component: () => import("@/views/dashboard/DashboardPage.vue"),
  },
  {
    path: "/system/settings/users/:id/setup-authenticator",
    name: "system.settings.user.view.setup",
    meta: {
      title: setTitle("Setup User Authenticator"),
      authRequired: true,
    },
    component: () =>
      import("@/views/system/settings/users/SetUpAuthenticator.vue"),
  },
];

const notificationRoutes = [
  {
    path: "/pages/notifications",
    name: "notifications.center",
    meta: {
      title: setTitle("Notifications"),
      authRequired: true,
    },
    component: () => import("@/views/notifications/NotificationsCenter.vue"),
  },
];

const complianceRoutes = [
  {
    path: "/compliance/breaches",
    name: "compliance.breaches",
    meta: {
      title: setTitle("Compliance Breaches"),
      authRequired: true,
      permission: "Can view Compliance Breaches",
    },
    component: () => import("@/views/compliance/ComplianceBreaches.vue"),
  },
  {
    path: "/compliance/breaches/create",
    name: "compliance.breach.create",
    meta: {
      title: setTitle("Log Compliance Breach"),
      authRequired: true,
      permission: "Can create Compliance Breaches",
    },
    component: () => import("@/views/compliance/CreateComplianceBreach.vue"),
  },
  {
    path: "/compliance/breaches/:id",
    name: "compliance.breach.detail",
    meta: {
      title: setTitle("Compliance Breach Detail"),
      authRequired: true,
      permission: "Can view Compliance Breaches",
    },
    component: () => import("@/views/compliance/ComplianceBreachDetail.vue"),
  },
  {
    path: "/compliance/breaches/:id/edit",
    name: "compliance.breach.edit",
    meta: {
      title: setTitle("Edit Compliance Breach"),
      authRequired: true,
      permission: "Can edit Compliance Breaches",
    },
    component: () => import("@/views/compliance/CreateComplianceBreach.vue"),
  },
];

const RiskRoutes = [
  {
    path: "/risk/register",
    name: "risk.register",
    meta: {
      title: setTitle("Risk register"),
      authRequired: true,
      permission: "Can view Risk Register",
    },
    component: () => import("@/views/risk-register/RiskRegister.vue"),
  },
  {
    path: "/risk/register/:id",
    name: "risk.register.view",
    meta: {
      title: setTitle("Risk register"),
      authRequired: true,
      permission: "Can view Risk Register",
    },
    component: () => import("@/views/risk-register/ViewRisk.vue"),
  },
  {
    path: "/risk/register/create",
    name: "risk.register.create",
    meta: { authRequired: true, permission: "Can create Risk Register" },
    component: () => import("@/views/risk-register/AddRisk.vue"),
  },
  {
    path: "/risk/register/:id/edit",
    name: "risk.register.edit",
    meta: { authRequired: true, permission: "Can edit Risk Register" },
    component: () => import("@/views/risk-register/AddRisk.vue"),
  },
  {
    path: "/risk/monitoring",
    name: "risk.monitoring",
    meta: { authRequired: true, permission: "Can view Risk Register" },
    component: () => import("@/views/risk-register/RiskAppetitePanel.vue"),
  },
  {
    path: "/risk/heat-map",
    name: "risk.heat-map",
    meta: {
      title: setTitle("Risk Heat Map"),
      authRequired: true,
      permission: "Can view Risk Register",
    },
    component: () => import("@/views/risk-register/RiskHeatMapPage.vue"),
  },
  {
    path: "/risk/appetite-tolerance",
    name: "risk.appetite-tolerance",
    meta: {
      title: setTitle("Risk Appetite & Tolerance"),
      authRequired: true,
      permission: "Can view Risk Appetite",
    },
    component: () => import("@/views/risk-register/RiskAppetiteToleranceDashboard.vue"),
  },
  {
    path: "/risk/kris",
    name: "risk.kris",
    meta: {
      title: setTitle("KRI Catalogue"),
      authRequired: true,
      permission: "Can view Risk Appetite",
    },
    component: () => import("@/views/risk-register/KriCatalogue.vue"),
  },
  {
    path: "/risk/treatment",
    name: "risk.treatment.list",
    meta: {
      title: setTitle("Risk Treatment"),
      authRequired: true,
      permission: "Can view Risk Treatment",
    },
    component: () => import("@/views/risk-register/treatment/RiskTreatmentList.vue"),
  },
  {
    path: "/risk/treatment/create",
    name: "risk.treatment.create",
    meta: { authRequired: true, permission: "Can create Risk Treatment" },
    component: () => import("@/views/risk-register/treatment/AddRiskTreatment.vue"),
  },
  {
    path: "/risk/treatment/:id",
    name: "risk.treatment.view",
    meta: {
      title: setTitle("Risk Treatment Plan"),
      authRequired: true,
      permission: "Can view Risk Treatment",
    },
    component: () => import("@/views/risk-register/treatment/ViewRiskTreatment.vue"),
  },
  {
    path: "/risk/assessments",
    name: "risk.assessments.list",
    meta: {
      title: setTitle("Risk Assessments"),
      authRequired: true,
      permission: "Can view Risk Assessments",
    },
    component: () => import("@/views/risk-register/assessments/RiskAssessmentList.vue"),
  },
  {
    path: "/risk/assessments/create",
    name: "risk.assessments.create",
    meta: { authRequired: true, permission: "Can create Risk Assessments" },
    component: () => import("@/views/risk-register/assessments/AddRiskAssessment.vue"),
  },
  {
    path: "/risk/assessments/:id",
    name: "risk.assessments.view",
    meta: {
      title: setTitle("Risk Assessment"),
      authRequired: true,
      permission: "Can view Risk Assessments",
    },
    component: () => import("@/views/risk-register/assessments/ViewRiskAssessment.vue"),
  },
  {
    path: "/risk/assessments/:id/edit",
    name: "risk.assessments.edit",
    meta: { authRequired: true, permission: "Can edit Risk Assessments" },
    component: () => import("@/views/risk-register/assessments/AddRiskAssessment.vue"),
  },
  {
    path: "/risk/emerging",
    name: "risk.emerging.list",
    meta: {
      title: setTitle("Emerging Risks"),
      authRequired: true,
      permission: "Can view Emerging Risks",
    },
    component: () => import("@/views/risk-register/emerging/EmergingRiskList.vue"),
  },
  {
    path: "/risk/emerging/create",
    name: "risk.emerging.create",
    meta: { authRequired: true, permission: "Can create Emerging Risks" },
    component: () => import("@/views/risk-register/emerging/AddEmergingRisk.vue"),
  },
  {
    path: "/risk/emerging/:id",
    name: "risk.emerging.view",
    meta: {
      title: setTitle("Emerging Risk"),
      authRequired: true,
      permission: "Can view Emerging Risks",
    },
    component: () => import("@/views/risk-register/emerging/ViewEmergingRisk.vue"),
  },
  {
    path: "/risk/emerging/:id/edit",
    name: "risk.emerging.edit",
    meta: { authRequired: true, permission: "Can edit Emerging Risks" },
    component: () => import("@/views/risk-register/emerging/AddEmergingRisk.vue"),
  },
];

const incidentRoutes = [
  {
    path: "/incident/listing",
    name: "incident.listing",
    meta: {
      title: setTitle("Incident Listing"),
      authRequired: true,
      permission: "Can view Incidents",
    },
    component: () => import("@/views/incident/IncidentDashboard.vue"),
  },
  {
    path: "/incident/report/:id?",
    name: "incident.report",
    meta: {
      title: setTitle("Report Incident"),
      authRequired: true,
      permission: "Can create Incidents",
    },
    component: () => import("@/views/incident/ReportIncident.vue"),
  },

  {
    path: "/incident/:id",
    name: "incident.detail",
    meta: {
      title: setTitle("Incident Detail"),
      authRequired: true,
      permission: "Can view Incidents",
      menu: "incident.listing",
    },
    component: () => import("@/views/incident/IncidentDetail.vue"),
  },
];

const investigationRoutes = [
  {
    path: "/investigations",
    name: "investigation.list",
    meta: { title: setTitle("Investigations"), authRequired: true, permission: "Can view Investigations" },
    component: () => import("@/views/incident/InvestigationList.vue"),
  },
  {
    path: "/investigations/create",
    name: "investigation.create",
    meta: { title: setTitle("Open Investigation"), authRequired: true, permission: "Can create Investigations" },
    component: () => import("@/views/incident/AddInvestigation.vue"),
  },
  {
    path: "/investigations/:id/view",
    name: "investigation.view",
    meta: { title: setTitle("View Investigation"), authRequired: true, permission: "Can view Investigations" },
    component: () => import("@/views/incident/ViewInvestigation.vue"),
  },
];

const lossEventRoutes = [
  {
    path: "/loss-events",
    name: "loss-event.list",
    meta: { title: setTitle("Loss Events"), authRequired: true, permission: "Can view Loss Events" },
    component: () => import("@/views/incident/LossEventList.vue"),
  },
  {
    path: "/loss-events/create",
    name: "loss-event.create",
    meta: { title: setTitle("Log Loss Event"), authRequired: true, permission: "Can create Loss Events" },
    component: () => import("@/views/incident/AddLossEvent.vue"),
  },
  {
    path: "/loss-events/:id/view",
    name: "loss-event.view",
    meta: { title: setTitle("View Loss Event"), authRequired: true, permission: "Can view Loss Events" },
    component: () => import("@/views/incident/ViewLossEvent.vue"),
  },
];

const fraudRoutes = [
  {
    path: "/fraud-management/list",
    name: "fraud-management.list",
    meta: {
      title: setTitle("Fraud Management"),
      authRequired: true,
      permission: "Can view Fraud",
    },
    component: () => import("@/views/fraud-management/FraudList.vue"),
  },
  {
    path: "/fraud-management/risks",
    name: "fraud-management.risks",
    meta: {
      title: setTitle("Fraud Risks"),
      authRequired: true,
      permission: "Can view Risk Register",
    },
    component: () => import("@/views/fraud-management/FraudRiskList.vue"),
  },
  {
    path: "/fraud-management/submit",
    name: "fraud-management.submit",
    meta: {
      title: setTitle("Submit Fraud Case"),
      authRequired: true,
      permission: "Can create Fraud",
    },
    component: () => import("@/views/fraud-management/FraudSubmissionForm.vue"),
  },
  {
    path: "/fraud-management/edit/:id",
    name: "fraud-management.edit",
    meta: {
      title: setTitle("Edit Fraud Case"),
      authRequired: true,
      permission: "Can edit Fraud",
      menu: "fraud-management.list",
    },
    component: () => import("@/views/fraud-management/FraudSubmissionForm.vue"),
  },
  {
    path: "/fraud-management/:id",
    name: "fraud-management.view",
    meta: {
      title: setTitle("View Fraud Case"),
      authRequired: true,
      permission: "Can view Fraud",
      menu: "fraud-management.list",
    },
    component: () => import("@/views/fraud-management/FraudDetailView.vue"),
  },
];
const executiveDashboardRoutes = [
  {
    path: "/executive/dashboard",
    name: "executive.dashboard",
    meta: {
      title: setTitle("Executive Dashboard"),
      authRequired: true,
      permission: "Can view Executive Dashboard",
    },
    component: () => import("@/views/executive/Dashboard.vue"),
  },
];
const boardRoutes = [
  {
    path: "/board/dashboard",
    name: "board.dashboard",
    meta: { title: setTitle("Board Risk Dashboard"), authRequired: true, permission: "Can view Board" },
    component: () => import("@/views/board/BoardRiskDashboard.vue"),
  },
  {
    path: "/board/committees",
    name: "board.committees",
    meta: { title: setTitle("Board & Committee Configuration"), authRequired: true, permission: "Can view Board" },
    component: () => import("@/views/board/CommitteeSetup.vue"),
  },
  {
    path: "/board/matters",
    name: "board.matters",
    meta: { title: setTitle("Committee Matters"), authRequired: true, permission: "Can view Board" },
    component: () => import("@/views/board/CommitteeMatters.vue"),
  },
  {
    path: "/board/matters/create",
    name: "board.matters.create",
    meta: { title: setTitle("Table a Matter"), authRequired: true, permission: "Can create Board" },
    component: () => import("@/views/board/CommitteeMatterForm.vue"),
  },
  {
    path: "/board/matters/:id",
    name: "board.matters.view",
    meta: { title: setTitle("Committee Matter"), authRequired: true, permission: "Can view Board" },
    component: () => import("@/views/board/CommitteeMatterDetail.vue"),
  },
  {
    path: "/board/matters/:id/edit",
    name: "board.matters.edit",
    meta: { title: setTitle("Edit Committee Matter"), authRequired: true, permission: "Can edit Board" },
    component: () => import("@/views/board/CommitteeMatterForm.vue"),
  },
  {
    path: "/board/escalations",
    name: "board.escalations",
    meta: { title: setTitle("Matters for Escalation"), authRequired: true, permission: "Can view Board" },
    component: () => import("@/views/board/MattersForEscalation.vue"),
  },
  {
    path: "/board/decisions-actions",
    name: "board.decisions-actions",
    meta: { title: setTitle("Decisions & Actions"), authRequired: true, permission: "Can view Board" },
    component: () => import("@/views/board/DecisionsActions.vue"),
  },
  {
    path: "/board/actions/:id",
    name: "board.actions.view",
    meta: { title: setTitle("Board Action"), authRequired: true, permission: "Can view Board", menu: "board.decisions-actions" },
    component: () => import("@/views/board/BoardActionDetail.vue"),
  },
  {
    path: "/board/governance-reports",
    name: "board.governance-reports",
    meta: { title: setTitle("Governance Reports"), authRequired: true, permission: "Can view Board" },
    component: () => import("@/views/board/GovernanceReports.vue"),
  },
];
const reportRoutes = [
  {
    path: "/reports/generated",
    name: "reports.generated",
    meta: {
      title: setTitle("Regulatory Reporting"),
      authRequired: true,
      permission: "Can view Reports",
      menu: "previous", // opened from every Reporting suite and Governance Reports
    },
    component: () => import("@/views/reports/GeneratedReports.vue"),
  },
  {
    path: "/reports/builder",
    name: "reports.builder",
    meta: {
      title: setTitle("Regulatory Reporting"),
      authRequired: true,
      permission: "Can create Reports",
      menu: "previous", // opened from every Reporting suite and Governance Reports
    },
    component: () => import("@/views/reports/ReportBuilder.vue"),
  },
  {
    path: "/reports/:id",
    name: "reports.view",
    meta: {
      title: setTitle("Report"),
      authRequired: true,
      permission: "Can view Reports",
      menu: "previous", // opened from every Reporting suite and Governance Reports
    },
    component: () => import("@/views/reports/ReportView.vue"),
  },
  // Reporting sidebar suites (RCS-131) — one named route per suite, one shared page.
  ...REPORTING_SUITES.map((suite) => ({
    path: suite.path,
    name: suite.routeName,
    meta: { title: setTitle(`${suite.label} Reporting`), authRequired: true, permission: "Can view Reports" },
    component: () => import("@/views/reports/ReportingSuite.vue"),
    props: { suiteKey: suite.key },
  })),
  {
    path: "/reporting/scheduled-reports",
    name: "reporting.scheduled-reports",
    meta: { title: setTitle("Scheduled Reports"), authRequired: true, permission: "Can view Scheduled Reports" },
    component: () => import("@/views/reports/schedules/ScheduledReports.vue"),
  },
  {
    path: "/reporting/scheduled-reports/new",
    name: "reporting.scheduled-reports.create",
    meta: { title: setTitle("New Report Schedule"), authRequired: true, permission: "Can create Scheduled Reports" },
    component: () => import("@/views/reports/schedules/ReportScheduleForm.vue"),
  },
  {
    path: "/reporting/scheduled-reports/:id/edit",
    name: "reporting.scheduled-reports.edit",
    meta: { title: setTitle("Edit Report Schedule"), authRequired: true, permission: "Can edit Scheduled Reports" },
    component: () => import("@/views/reports/schedules/ReportScheduleForm.vue"),
  },
  {
    path: "/reporting/scheduled-reports/:id",
    name: "reporting.scheduled-reports.view",
    meta: { title: setTitle("Report Schedule"), authRequired: true, permission: "Can view Scheduled Reports" },
    component: () => import("@/views/reports/schedules/ReportScheduleDetail.vue"),
  },
];

const escalationManagementRoutes = [
  {
    path: "/escalations",
    name: "escalation.list",
    meta: {
      title: setTitle("Escalations Management"),
      authRequired: true,
      permission: "Can view Escalations",
    },
    component: () => import("@/views/escalations/EscalationManagement.vue"),
  },
];
const specialisedComplianceRoutes = [
  {
    path: "/specialised-compliance/aml",
    name: "specialised.aml",
    meta: {
      title: setTitle("AML / Financial Crime"),
      authRequired: true,
      permission: "Can view AML",
    },
    component: () => import("@/views/specialised-compliance/AmlFinancialCrime.vue"),
  },
  {
    path: "/specialised-compliance/aml/cases/:id",
    name: "specialised.aml.case.view",
    meta: {
      title: setTitle("AML Case"),
      authRequired: true,
      permission: "Can view AML",
    },
    component: () => import("@/views/specialised-compliance/AmlCaseDetail.vue"),
  },
  {
    path: "/specialised-compliance/data-privacy",
    name: "specialised.data-privacy",
    meta: {
      title: setTitle("Data Privacy"),
      authRequired: true,
      permission: "Can view Data Privacy",
    },
    component: () => import("@/views/specialised-compliance/DataPrivacy.vue"),
  },
  {
    path: "/specialised-compliance/data-privacy/incidents/:id",
    name: "specialised.data-privacy.incident.view",
    meta: {
      title: setTitle("Privacy Incident"),
      authRequired: true,
      permission: "Can view Data Privacy",
    },
    component: () => import("@/views/specialised-compliance/PrivacyIncidentDetail.vue"),
  },
  {
    path: "/specialised-compliance/market-conduct",
    name: "specialised.market-conduct",
    meta: {
      title: setTitle("Market Conduct"),
      authRequired: true,
      permission: "Can view Market Conduct",
    },
    component: () => import("@/views/specialised-compliance/MarketConduct.vue"),
  },
  {
    path: "/specialised-compliance/market-conduct/cases/:id",
    name: "specialised.market-conduct.case.view",
    meta: {
      title: setTitle("Conduct Case"),
      authRequired: true,
      permission: "Can view Market Conduct",
    },
    component: () => import("@/views/specialised-compliance/ConductCaseDetail.vue"),
  },
  {
    path: "/specialised-compliance/third-party-risk",
    name: "specialised.third-party-risk",
    meta: {
      title: setTitle("Third-Party Risk"),
      authRequired: true,
      permission: "Can view Third-Party Risk",
    },
    component: () => import("@/views/specialised-compliance/ThirdPartyRisk.vue"),
  },
  {
    path: "/specialised-compliance/third-party-risk/reviews/:id",
    name: "specialised.third-party-risk.review.view",
    meta: {
      title: setTitle("Vendor Review"),
      authRequired: true,
      permission: "Can view Third-Party Risk",
    },
    component: () => import("@/views/specialised-compliance/VendorReviewDetail.vue"),
  },
  {
    path: "/specialised-compliance/technology-cyber-risk",
    name: "specialised.technology-cyber-risk",
    meta: {
      title: setTitle("Technology & Cyber Risk"),
      authRequired: true,
      permission: "Can view Technology Risk",
    },
    component: () => import("@/views/specialised-compliance/TechnologyCyberRisk.vue"),
  },
  {
    path: "/specialised-compliance/technology-cyber-risk/vulnerabilities/:id",
    name: "specialised.technology-cyber-risk.vulnerability.view",
    meta: {
      title: setTitle("Vulnerability"),
      authRequired: true,
      permission: "Can view Technology Risk",
    },
    component: () => import("@/views/specialised-compliance/VulnerabilityDetail.vue"),
  },
  {
    path: "/specialised-compliance/model-risk",
    name: "specialised.model-risk",
    meta: {
      title: setTitle("Model Risk"),
      authRequired: true,
      permission: "Can view Model Risk",
    },
    component: () => import("@/views/specialised-compliance/ModelRisk.vue"),
  },
  {
    path: "/specialised-compliance/model-risk/validations/:id",
    name: "specialised.model-risk.validation.view",
    meta: {
      title: setTitle("Model Validation"),
      authRequired: true,
      permission: "Can view Model Risk",
    },
    component: () => import("@/views/specialised-compliance/ModelValidationDetail.vue"),
  },
  {
    path: "/specialised-compliance/conflict-of-interest",
    name: "specialised.conflict-of-interest",
    meta: {
      title: setTitle("Conflict of Interest"),
      authRequired: true,
      permission: "Can view Conflict of Interest",
    },
    component: () => import("@/views/specialised-compliance/ConflictOfInterest.vue"),
  },
  {
    path: "/specialised-compliance/conflict-of-interest/:id",
    name: "specialised.conflict-of-interest.view",
    meta: {
      title: setTitle("COI Declaration"),
      authRequired: true,
      permission: "Can view Conflict of Interest",
    },
    component: () => import("@/views/specialised-compliance/CoiDeclarationDetail.vue"),
  },
];
const AuditPlanRoutes = [
  {
    path: "/audit/plans",
    name: "audit.plans",
    meta: {
      title: setTitle("Audit plans"),
      authRequired: true,
      permission: "Can view Audits",
    },
    component: () => import("@/views/audit/plans/AuditPlansList.vue"),
  },
  {
    path: "/audit/plans/create",
    name: "audit.plans.create",
    meta: {
      title: setTitle("Audit plans"),
      authRequired: true,
      permission: "Can create Audits",
    },
    component: () => import("@/views/audit/plans/AddAuditPlan.vue"),
  },
  {
    path: "/audit/plans/:id/edit",
    name: "audit.plans.edit",
    meta: {
      title: setTitle("Audit plans"),
      authRequired: true,
      permission: "Can edit Audits",
    },
    component: () => import("@/views/audit/plans/AddAuditPlan.vue"),
  },
  {
    path: "/audit/plans/:id/view",
    name: "audit.plans.view",
    meta: {
      title: setTitle("Audit plans"),
      authRequired: true,
      permission: "Can view Audits",
    },
    component: () => import("@/views/audit/plans/ViewAuditPlan.vue"),
  },
  {
    path: "/audit/findings",
    name: "audit.findings",
    meta: {
      title: setTitle("Audit Findings"),
      authRequired: true,
      permission: "Can view Audits",
    },
    component: () => import("@/views/audit/plans/AuditFindingsList.vue"),
  },
  {
    path: "/audit/findings/:id",
    name: "audit.findings.view",
    meta: {
      title: setTitle("Audit Findings"),
      authRequired: true,
      permission: "Can view Audits",
    },
    component: () => import("@/views/audit/plans/FindingDetailPage.vue"),
  },
  {
    path: "/audit/execution",
    name: "audit.execution",
    meta: {
      title: setTitle("Audit Execution"),
      authRequired: true,
      permission: "Can view Audits",
    },
    component: () => import("@/views/audit/plans/AuditExecution.vue"),
  },
  {
    path: "/audit/follow-up",
    name: "audit.follow-up",
    meta: {
      title: setTitle("Audit Follow-Up"),
      authRequired: true,
      permission: "Can view Audits",
    },
    component: () => import("@/views/audit/plans/AuditFollowUp.vue"),
  },
];

const systemRoutes = [
  {
    path: "/system/audit-trail",
    name: "system.audit-trail",
    meta: {
      title: setTitle("Audit Trail"),
      authRequired: true,
      permission: "Can view Audit Trail",
    },
    component: () => import("@/views/system/audit-trail/AuditTrail.vue"),
  },
  {
    path: "/system/audit-trail/:id",
    name: "audit-trail.view",
    meta: {
      title: setTitle("Audit Trail"),
      authRequired: true,
      permission: "Can view Audit Trail",
    },
    component: () =>
      import("@/views/system/audit-trail/AuditTrailDetailView.vue"),
  },
  {
    path: "/system/users",
    name: "system.users",
    meta: {
      title: setTitle("System Users"),
      authRequired: true,
      permission: "Can view Users",
    },
    component: () =>
      import("@/views/system/users/SystemUsers.vue"),
  },
  {
    path: "/system/users/:id/view",
    name: "system.user.view",
    meta: {
      title: setTitle("System Users"),
      authRequired: true,
      permission: "Can view Users",
    },
    component: () =>
      import("@/views/system/users/ViewSystemUsers.vue"),
  },
  {
    path: "/system/roles",
    name: "system.roles",
    meta: {
      title: setTitle("Roles & Permissions"),
      authRequired: true,
      permission: "Can view Roles",
    },
    component: () =>
      import("@/views/system/roles/RolesPermissions.vue"),
  },
  // Administration (RCS-133) — Data Scope is a governed rule set, so it
  // reuses the same ConfigPage.vue every Config & Control Centre namespace
  // renders from (route.meta.namespace), not a bespoke page.
  {
    path: "/admin/data-scope",
    name: "admin.data-scope",
    meta: { title: setTitle("Data Scope"), authRequired: true,
      permission: "Can view Settings", namespace: "access-scope" },
    component: () => import("@/views/config/ConfigPage.vue"),
  },
  {
    path: "/admin/system-health",
    name: "admin.system-health",
    meta: { title: setTitle("System Health"), authRequired: true, permission: "Can view System Health" },
    component: () => import("@/views/admin/SystemHealthView.vue"),
  },
  {
    path: "/admin/integration-logs",
    name: "admin.integration-logs",
    meta: { title: setTitle("Integration Logs"), authRequired: true, permission: "Can view System Health" },
    component: () => import("@/views/admin/IntegrationLogsView.vue"),
  },
  {
    path: "/admin/deleted-items",
    name: "admin.deleted-items",
    meta: { title: setTitle("Deleted Items"), authRequired: true, permission: "Can view Deleted Items" },
    component: () => import("@/views/admin/DeletedItemsView.vue"),
  },
];

// One explicit, individually-named route per lookup type — each gets its own
// sidebar menu item and highlights independently, instead of every lookup
// sharing a single ":type" route (which would make every sidebar link light
// up together, since the menu's active-state check matches by route name).
const referenceDataRoutes = Object.entries(LOOKUP_CONFIG).map(([type, config]) => ({
  path: `/system/reference-data/${type}`,
  name: `reference-data.${type}`,
  meta: {
    title: setTitle(config.label),
    authRequired: true,
    permission: "Can view Settings",
    lookupType: type,
  },
  component: () => import("@/views/system/lookups/LookupManager.vue"),
}));

const CapaRoutes = [
  {
    path: "/capa-management/list",
    name: "capa.list",
    meta: {
      title: setTitle("CAPA Management"),
      authRequired: true,
      permission: "Can view CAPA",
    },
    component: () => import("@/views/capa-management/CapaListView.vue"),
  },
  {
    path: "/capa-management/create",
    name: "capa.create",
    meta: {
      title: setTitle("Create CAPA"),
      authRequired: true,
      permission: "Can create CAPA",
    },
    component: () => import("@/views/capa-management/CapaForm.vue"),
  },
  {
    path: "/capa-management/edit/:id",
    name: "capa.edit",
    meta: {
      title: setTitle("Edit CAPA"),
      authRequired: true,
      permission: "Can edit CAPA",
      menu: "capa.list",
    },
    component: () => import("@/views/capa-management/CapaForm.vue"),
  },
  {
    path: "/capa-management/dashboard",
    name: "capa.dashboard",
    meta: {
      title: setTitle("CAPA Dashboard Analytics"),
      authRequired: true,
      permission: "Can view CAPA",
    },
    component: () => import("@/views/capa-management/CapaDashboard.vue"),
  },
  {
    path: "/capa-management/:id",
    name: "capa.view",
    meta: {
      title: setTitle("View CAPA"),
      authRequired: true,
      permission: "Can view CAPA",
      menu: "capa.list",
    },
    component: () => import("@/views/capa-management/CapaDetailView.vue"),
  },
  {
    path: "/capa-management/:id/root-cause-analysis",
    name: "capa.root-cause-analysis",
    meta: {
      title: setTitle("Root Cause Analysis"),
      authRequired: true,
      permission: "Can edit CAPA",
      menu: "capa.list",
    },
    component: () =>
      import("@/views/capa-management/CapaRootCauseAnalysis.vue"),
  },
  {
    path: "/capa-management/:id/approval",
    name: "capa.approval",
    meta: {
      title: setTitle("CAPA Approval"),
      authRequired: true,
      permission: "Can approve CAPA",
      menu: "capa.list",
    },
    component: () => import("@/views/capa-management/CapaApproval.vue"),
  },
  {
    path: "/capa-management/:id/documents",
    name: "capa.documents",
    meta: {
      title: setTitle("CAPA Documents"),
      authRequired: true,
      permission: "Can view CAPA",
      menu: "capa.list",
    },
    component: () => import("@/views/capa-management/CapaDocumentCenter.vue"),
  },
  {
    path: "/capa-management/validation",
    name: "capa.validation.queue",
    meta: {
      title: setTitle("CAPA Validation"),
      authRequired: true,
      permission: "Can approve CAPA",
    },
    component: () => import("@/views/capa-management/CapaValidationQueue.vue"),
  },
  {
    path: "/capa-management/effectiveness-review",
    name: "capa.effectiveness.queue",
    meta: {
      title: setTitle("CAPA Effectiveness Review"),
      authRequired: true,
      permission: "Can approve CAPA",
    },
    component: () => import("@/views/capa-management/CapaEffectivenessQueue.vue"),
  },
];

// Findings and Root Cause Analysis are independent features — each its own
// route tree, never sharing a page with the other or with CAPA. CAPA links
// to a Finding only via its existing trigger_source mechanism.
const FindingRoutes = [
  {
    path: "/findings",
    name: "findings.list",
    meta: {
      title: setTitle("Findings"),
      authRequired: true,
      permission: "Can view Findings",
    },
    component: () => import("@/views/findings/FindingList.vue"),
  },
  {
    path: "/findings/create",
    name: "findings.create",
    meta: { authRequired: true, permission: "Can create Findings" },
    component: () => import("@/views/findings/AddFinding.vue"),
  },
  {
    path: "/findings/:id",
    name: "findings.view",
    meta: {
      title: setTitle("Finding"),
      authRequired: true,
      permission: "Can view Findings",
    },
    component: () => import("@/views/findings/ViewFinding.vue"),
  },
  {
    path: "/findings/:id/edit",
    name: "findings.edit",
    meta: { authRequired: true, permission: "Can edit Findings" },
    component: () => import("@/views/findings/AddFinding.vue"),
  },
];

const RcaRoutes = [
  {
    path: "/root-cause-analysis",
    name: "rca.list",
    meta: {
      title: setTitle("Root Cause Analysis"),
      authRequired: true,
      permission: "Can view Root Cause Analysis",
    },
    component: () => import("@/views/findings/rca/RcaList.vue"),
  },
  {
    path: "/root-cause-analysis/create",
    name: "rca.create",
    meta: { authRequired: true, permission: "Can create Root Cause Analysis" },
    component: () => import("@/views/findings/rca/AddRca.vue"),
  },
  {
    path: "/root-cause-analysis/:id",
    name: "rca.view",
    meta: {
      title: setTitle("Root Cause Analysis"),
      authRequired: true,
      permission: "Can view Root Cause Analysis",
    },
    component: () => import("@/views/findings/rca/ViewRca.vue"),
  },
  {
    path: "/root-cause-analysis/:id/edit",
    name: "rca.edit",
    meta: { authRequired: true, permission: "Can edit Root Cause Analysis" },
    component: () => import("@/views/findings/rca/AddRca.vue"),
  },
];

// Control Library is its own independent route tree — each other Controls &
// Assurance sidebar item (Mapping, Testing, Continuous Monitoring,
// Exceptions, Effectiveness) has its own separate route tree below, under a
// distinct top-level path segment so it never collides with /controls/:id.
const ControlRoutes = [
  {
    path: "/controls",
    name: "controls.list",
    meta: {
      title: setTitle("Control Library"),
      authRequired: true,
      permission: "Can view Controls",
    },
    component: () => import("@/views/controls/ControlList.vue"),
  },
  {
    path: "/controls/create",
    name: "controls.create",
    meta: { authRequired: true, permission: "Can create Controls" },
    component: () => import("@/views/controls/AddControl.vue"),
  },
  {
    path: "/controls/:id",
    name: "controls.view",
    meta: {
      title: setTitle("Control"),
      authRequired: true,
      permission: "Can view Controls",
    },
    component: () => import("@/views/controls/ViewControl.vue"),
  },
  {
    path: "/controls/:id/edit",
    name: "controls.edit",
    meta: { authRequired: true, permission: "Can edit Controls" },
    component: () => import("@/views/controls/AddControl.vue"),
  },
];

const ControlMappingRoutes = [
  {
    path: "/control-mapping",
    name: "controls.mapping",
    meta: {
      title: setTitle("Control Mapping"),
      authRequired: true,
      permission: "Can view Controls",
    },
    component: () => import("@/views/controls/ControlMappingList.vue"),
  },
];

const ControlTestRoutes = [
  {
    path: "/control-tests",
    name: "controls.tests.list",
    meta: {
      title: setTitle("Control Testing"),
      authRequired: true,
      permission: "Can view Control Testing",
    },
    component: () => import("@/views/controls/ControlTestList.vue"),
  },
  {
    path: "/control-tests/create",
    name: "controls.tests.create",
    meta: { authRequired: true, permission: "Can create Control Testing" },
    component: () => import("@/views/controls/AddControlTest.vue"),
  },
  {
    path: "/control-tests/:id",
    name: "controls.tests.view",
    meta: {
      title: setTitle("Control Test"),
      authRequired: true,
      permission: "Can view Control Testing",
    },
    component: () => import("@/views/controls/ViewControlTest.vue"),
  },
];

const ControlMonitoringRoutes = [
  {
    path: "/control-monitoring",
    name: "controls.monitoring",
    meta: {
      title: setTitle("Continuous Control Monitoring"),
      authRequired: true,
      permission: "Can view Control Monitoring",
    },
    component: () => import("@/views/controls/ControlMonitoringList.vue"),
  },
];

const ControlExceptionRoutes = [
  {
    path: "/control-exceptions",
    name: "controls.exceptions.list",
    meta: {
      title: setTitle("Control Exceptions"),
      authRequired: true,
      permission: "Can view Control Exceptions",
    },
    component: () => import("@/views/controls/ControlExceptionList.vue"),
  },
  {
    path: "/control-exceptions/create",
    name: "controls.exceptions.create",
    meta: { authRequired: true, permission: "Can create Control Exceptions" },
    component: () => import("@/views/controls/AddControlException.vue"),
  },
  {
    path: "/control-exceptions/:id",
    name: "controls.exceptions.view",
    meta: {
      title: setTitle("Control Exception"),
      authRequired: true,
      permission: "Can view Control Exceptions",
    },
    component: () => import("@/views/controls/ViewControlException.vue"),
  },
];

const ControlEffectivenessRoutes = [
  {
    path: "/control-effectiveness",
    name: "controls.effectiveness",
    meta: {
      title: setTitle("Control Effectiveness"),
      authRequired: true,
      permission: "Can view Controls",
    },
    component: () => import("@/views/controls/ControlEffectivenessDashboard.vue"),
  },
];

// Governance — each of the 7 sidebar items is its own independent route tree.
const RegulationRoutes = [
  {
    path: "/governance/regulations",
    name: "governance.regulations.list",
    meta: { title: setTitle("Regulatory Universe"), authRequired: true, permission: "Can view Regulatory Universe" },
    component: () => import("@/views/governance/RegulationList.vue"),
  },
  {
    path: "/governance/regulations/create",
    name: "governance.regulations.create",
    meta: { authRequired: true, permission: "Can create Regulatory Universe" },
    component: () => import("@/views/governance/AddRegulation.vue"),
  },
  {
    path: "/governance/regulations/:id",
    name: "governance.regulations.view",
    meta: { title: setTitle("Regulation"), authRequired: true, permission: "Can view Regulatory Universe" },
    component: () => import("@/views/governance/ViewRegulation.vue"),
  },
  {
    path: "/governance/regulations/:id/edit",
    name: "governance.regulations.edit",
    meta: { authRequired: true, permission: "Can edit Regulatory Universe" },
    component: () => import("@/views/governance/AddRegulation.vue"),
  },
];

const PolicyUniverseRoutes = [
  {
    path: "/governance/policy-universe",
    name: "governance.policy-universe.list",
    meta: { title: setTitle("Internal Policy Universe"), authRequired: true, permission: "Can view Policy Universe" },
    component: () => import("@/views/governance/PolicyUniverseList.vue"),
  },
  {
    path: "/governance/policy-universe/create",
    name: "governance.policy-universe.create",
    meta: { authRequired: true, permission: "Can create Policy Universe" },
    component: () => import("@/views/governance/AddPolicyUniverseEntry.vue"),
  },
  {
    path: "/governance/policy-universe/:id",
    name: "governance.policy-universe.view",
    meta: { title: setTitle("Policy Universe Entry"), authRequired: true, permission: "Can view Policy Universe" },
    component: () => import("@/views/governance/ViewPolicyUniverseEntry.vue"),
  },
  {
    path: "/governance/policy-universe/:id/edit",
    name: "governance.policy-universe.edit",
    meta: { authRequired: true, permission: "Can edit Policy Universe" },
    component: () => import("@/views/governance/AddPolicyUniverseEntry.vue"),
  },
];

const ObligationRoutes = [
  {
    path: "/governance/obligations",
    name: "governance.obligations.list",
    meta: { title: setTitle("Obligation Register"), authRequired: true, permission: "Can view Obligations" },
    component: () => import("@/views/governance/ObligationList.vue"),
  },
  {
    path: "/governance/obligations/create",
    name: "governance.obligations.create",
    meta: { authRequired: true, permission: "Can create Obligations" },
    component: () => import("@/views/governance/AddObligation.vue"),
  },
  {
    path: "/governance/obligations/:id",
    name: "governance.obligations.view",
    meta: { title: setTitle("Obligation"), authRequired: true, permission: "Can view Obligations" },
    component: () => import("@/views/governance/ViewObligation.vue"),
  },
  {
    path: "/governance/obligations/:id/edit",
    name: "governance.obligations.edit",
    meta: { authRequired: true, permission: "Can edit Obligations" },
    component: () => import("@/views/governance/AddObligation.vue"),
  },
];

const ObligationControlMappingRoutes = [
  {
    path: "/governance/obligation-control-mapping",
    name: "governance.obligation-control-mapping",
    meta: { title: setTitle("Obligation-Control Mapping"), authRequired: true, permission: "Can view Obligations" },
    component: () => import("@/views/governance/ObligationControlMappingList.vue"),
  },
];

const PolicyHierarchyRoutes = [
  {
    path: "/governance/policy-hierarchy",
    name: "governance.policy-hierarchy",
    meta: { title: setTitle("Policy Hierarchy"), authRequired: true, permission: "Can view Policy Universe" },
    component: () => import("@/views/governance/PolicyHierarchyTree.vue"),
  },
];

const RegulatoryChangeRoutes = [
  {
    path: "/governance/regulatory-changes",
    name: "governance.regulatory-changes.list",
    meta: { title: setTitle("Regulatory Change"), authRequired: true, permission: "Can view Regulatory Change" },
    component: () => import("@/views/governance/RegulatoryChangeList.vue"),
  },
  {
    path: "/governance/regulatory-changes/create",
    name: "governance.regulatory-changes.create",
    meta: { authRequired: true, permission: "Can create Regulatory Change" },
    component: () => import("@/views/governance/AddRegulatoryChange.vue"),
  },
  {
    path: "/governance/regulatory-changes/:id",
    name: "governance.regulatory-changes.view",
    meta: { title: setTitle("Regulatory Change"), authRequired: true, permission: "Can view Regulatory Change" },
    component: () => import("@/views/governance/ViewRegulatoryChange.vue"),
  },
  {
    path: "/governance/regulatory-changes/:id/edit",
    name: "governance.regulatory-changes.edit",
    meta: { authRequired: true, permission: "Can edit Regulatory Change" },
    component: () => import("@/views/governance/AddRegulatoryChange.vue"),
  },
];

const GovernanceCalendarRoutes = [
  {
    path: "/governance/calendar",
    name: "governance.calendar",
    meta: { title: setTitle("Governance Calendar"), authRequired: true, permission: "Can view Obligations" },
    component: () => import("@/views/governance/GovernanceCalendar.vue"),
  },
];

// Compliance — each of the 8 sidebar items is its own independent route tree.
const ComplianceAssessmentRoutes = [
  {
    path: "/compliance/assessments",
    name: "compliance.assessments.list",
    meta: { title: setTitle("Compliance Assessments"), authRequired: true, permission: "Can view Compliance Assessments" },
    component: () => import("@/views/compliance/ComplianceAssessmentList.vue"),
  },
  {
    path: "/compliance/assessments/create",
    name: "compliance.assessments.create",
    meta: { authRequired: true, permission: "Can create Compliance Assessments" },
    component: () => import("@/views/compliance/AddComplianceAssessment.vue"),
  },
  {
    path: "/compliance/assessments/:id",
    name: "compliance.assessments.view",
    meta: { title: setTitle("Compliance Assessment"), authRequired: true, permission: "Can view Compliance Assessments" },
    component: () => import("@/views/compliance/ViewComplianceAssessment.vue"),
  },
];

const ExternalRegulatoryComplianceRoutes = [
  {
    path: "/compliance/external-regulatory-compliance",
    name: "compliance.external-regulatory",
    meta: { title: setTitle("External Regulatory Compliance"), authRequired: true, permission: "Can view Obligations" },
    component: () => import("@/views/compliance/ExternalRegulatoryComplianceList.vue"),
  },
];

const InternalPolicyComplianceRoutes = [
  {
    path: "/compliance/internal-policy-compliance",
    name: "compliance.internal-policy",
    meta: { title: setTitle("Internal Policy Compliance"), authRequired: true, permission: "Can view Policy Universe" },
    component: () => import("@/views/compliance/InternalPolicyComplianceList.vue"),
  },
];

const ComplianceMonitoringRoutes = [
  {
    path: "/compliance/monitoring",
    name: "compliance.monitoring",
    meta: { title: setTitle("Compliance Monitoring"), authRequired: true, permission: "Can view Compliance Monitoring" },
    component: () => import("@/views/compliance/ComplianceMonitoringList.vue"),
  },
];

const ComplianceCalendarRoutes = [
  {
    path: "/compliance/calendar",
    name: "compliance.calendar",
    meta: { title: setTitle("Compliance Calendar"), authRequired: true, permission: "Can view Regulatory Returns" },
    component: () => import("@/views/compliance/ComplianceCalendar.vue"),
  },
];

const RegulatoryReturnRoutes = [
  {
    path: "/compliance/regulatory-returns",
    name: "compliance.regulatory-returns.list",
    meta: { title: setTitle("Regulatory Returns"), authRequired: true, permission: "Can view Regulatory Returns" },
    component: () => import("@/views/compliance/RegulatoryReturnList.vue"),
  },
  {
    path: "/compliance/regulatory-returns/create",
    name: "compliance.regulatory-returns.create",
    meta: { authRequired: true, permission: "Can create Regulatory Returns" },
    component: () => import("@/views/compliance/AddRegulatoryReturn.vue"),
  },
  {
    path: "/compliance/regulatory-returns/:id",
    name: "compliance.regulatory-returns.view",
    meta: { title: setTitle("Regulatory Return"), authRequired: true, permission: "Can view Regulatory Returns" },
    component: () => import("@/views/compliance/ViewRegulatoryReturn.vue"),
  },
  {
    path: "/compliance/regulatory-returns/:id/edit",
    name: "compliance.regulatory-returns.edit",
    meta: { authRequired: true, permission: "Can edit Regulatory Returns" },
    component: () => import("@/views/compliance/AddRegulatoryReturn.vue"),
  },
];

const LicenceRoutes = [
  {
    path: "/compliance/licences",
    name: "compliance.licences.list",
    meta: { title: setTitle("Licences & Registrations"), authRequired: true, permission: "Can view Licences" },
    component: () => import("@/views/compliance/LicenceList.vue"),
  },
  {
    path: "/compliance/licences/create",
    name: "compliance.licences.create",
    meta: { authRequired: true, permission: "Can create Licences" },
    component: () => import("@/views/compliance/AddLicence.vue"),
  },
  {
    path: "/compliance/licences/:id",
    name: "compliance.licences.view",
    meta: { title: setTitle("Licence"), authRequired: true, permission: "Can view Licences" },
    component: () => import("@/views/compliance/ViewLicence.vue"),
  },
  {
    path: "/compliance/licences/:id/edit",
    name: "compliance.licences.edit",
    meta: { authRequired: true, permission: "Can edit Licences" },
    component: () => import("@/views/compliance/AddLicence.vue"),
  },
];

const RegulatoryExaminationRoutes = [
  {
    path: "/compliance/regulatory-examinations",
    name: "compliance.regulatory-examinations.list",
    meta: { title: setTitle("Regulatory Examinations"), authRequired: true, permission: "Can view Regulatory Examinations" },
    component: () => import("@/views/compliance/RegulatoryExaminationList.vue"),
  },
  {
    path: "/compliance/regulatory-examinations/create",
    name: "compliance.regulatory-examinations.create",
    meta: { authRequired: true, permission: "Can create Regulatory Examinations" },
    component: () => import("@/views/compliance/AddRegulatoryExamination.vue"),
  },
  {
    path: "/compliance/regulatory-examinations/:id",
    name: "compliance.regulatory-examinations.view",
    meta: { title: setTitle("Regulatory Examination"), authRequired: true, permission: "Can view Regulatory Examinations" },
    component: () => import("@/views/compliance/ViewRegulatoryExamination.vue"),
  },
  {
    path: "/compliance/regulatory-examinations/:id/edit",
    name: "compliance.regulatory-examinations.edit",
    meta: { authRequired: true, permission: "Can edit Regulatory Examinations" },
    component: () => import("@/views/compliance/AddRegulatoryExamination.vue"),
  },
];

// Document viewer page (RCS-147): every attachment opens here via
// viewDocument(). Not a menu item — "previous" keeps the section the user
// came from highlighted. No permission meta: the API decides per file
// (FileAccessService) and the page shows its answer.
const fileRoutes = [
  {
    path: "/files/:fileId",
    name: "files.view",
    meta: {
      title: setTitle("View Document"),
      authRequired: true,
      menu: "previous",
    },
    component: () => import("@/views/files/DocumentViewerPage.vue"),
  },
];

const documentRoutes = [
  {
    path: "/documents",
    name: "documents.list",
    meta: {
      title: setTitle("Documents"),
      authRequired: true,
      permission: "Can view Documents",
    },
    component: () => import("@/views/documents/DocumentList.vue"),
  },
  {
    path: "/documents/create",
    name: "documents.create",
    meta: {
      title: setTitle("Create Document"),
      authRequired: true,
      permission: "Can create Documents",
    },
    component: () => import("@/views/documents/DocumentFormView.vue"),
  },
  {
    path: "/documents/:id",
    name: "documents.view",
    meta: {
      title: setTitle("View Document"),
      authRequired: true,
      permission: "Can view Documents",
    },
    component: () => import("@/views/documents/DocumentView.vue"),
  },
  {
    path: "/documents/:id/edit",
    name: "documents.edit",
    meta: {
      title: setTitle("Edit Document"),
      authRequired: true,
      permission: "Can edit Documents",
    },
    component: () => import("@/views/documents/DocumentFormView.vue"),
  },
];
const evidenceRoutes = [
  {
    path: "/evidence/vault",
    name: "evidence.vault",
    meta: {
      title: setTitle("Evidence Vault"),
      authRequired: true,
      permission: "Can view Evidence Vault",
    },
    component: () => import("@/views/documents/EvidenceVault.vue"),
  },
  {
    path: "/evidence/retention-archive",
    name: "evidence.retention-archive",
    meta: {
      title: setTitle("Retention / Archive"),
      authRequired: true,
      permission: "Can view Documents",
    },
    component: () => import("@/views/documents/RetentionArchive.vue"),
  },
];
const policySopRoutes = [
  {
    path: "/policies",
    name: "policy.list",
    component: () => import("@/views/policies/PoliciesList.vue"),
    meta: { authRequired: true,
      permission: "Can view Policies", title: "Policy Register" },
  },
  {
    path: "/policies/create",
    name: "policy.create",
    component: () => import("@/views/policies/AddPolicy.vue"),
    meta: { authRequired: true,
      permission: "Can create Policies", title: "Draft Policy" },
  },
  {
    path: "/policies/:id/edit",
    name: "policy.edit",
    component: () => import("@/views/policies/AddPolicy.vue"),
    meta: { authRequired: true,
      permission: "Can edit Policies", title: "Edit Policy" },
  },
  {
    path: "/policies/:id/view",
    name: "policy.view",
    component: () => import("@/views/policies/PolicyDetail.vue"),
    meta: { authRequired: true,
      permission: "Can view Policies", title: "View Policy" },
  },
  {
    path: "/policies/:id/acknowledge",
    name: "policy.acknowledge",
    component: () => import("@/views/policies/AcknowledgePolicy.vue"),
    meta: { authRequired: true,
      permission: "Can view Policies", title: "Acknowledge Policy" },
  },
  {
    path: "/sops",
    name: "sop.list",
    component: () => import("@/views/sops/SopList.vue"),
    meta: { authRequired: true,
      permission: "Can view SOPs", title: "SOP Register" },
  },
  {
    path: "/sops/create",
    name: "sop.create",
    component: () => import("@/views/sops/AddSop.vue"),
    meta: { authRequired: true,
      permission: "Can create SOPs", title: "Draft SOP" },
  },
  {
    path: "/sops/:id/edit",
    name: "sop.edit",
    component: () => import("@/views/sops/AddSop.vue"),
    meta: { authRequired: true,
      permission: "Can edit SOPs", title: "Edit SOP" },
  },
  {
    path: "/sops/:id/view",
    name: "sop.view",
    component: () => import("@/views/sops/SopDetail.vue"),
    meta: { authRequired: true,
      permission: "Can view SOPs", title: "View SOP" },
  },
  {
    path: "/sops/:id/acknowledge",
    name: "sop.acknowledge",
    component: () => import("@/views/sops/AcknowledgeSop.vue"),
    meta: { authRequired: true,
      permission: "Can view SOPs", title: "Acknowledge SOP" },
  },
  {
    path: "/policies-sops/acknowledgments",
    name: "policy.sop.acknowledgments",
    component: () =>
      import("@/views/acknowledgement/AcknowledgementDashboard.vue"),
    meta: { authRequired: true,
      permission: "Can view Policies", title: "Acknowledgments dashboard" },
  },
  {
    path: "/policies-sops/reviews",
    name: "policy.sop.reviews",
    component: () => import("@/views/policies/PolicyReviewsList.vue"),
    meta: { authRequired: true,
      permission: "Can view Policies", title: "Policy & SOP Reviews" },
  },
  {
    path: "/policies-sops/training-compliance",
    name: "policy.sop.training-compliance",
    component: () => import("@/views/policies/TrainingComplianceList.vue"),
    meta: { authRequired: true,
      permission: "Can view Policies", title: "Training Compliance" },
  },
  {
    path: "/policies-sops/exceptions",
    name: "policy.exceptions.list",
    component: () => import("@/views/policies/PolicyExceptionList.vue"),
    meta: { authRequired: true,
      permission: "Can view Policy Exceptions", title: "Exceptions & Waivers" },
  },
  {
    path: "/policies-sops/exceptions/create",
    name: "policy.exceptions.create",
    component: () => import("@/views/policies/AddPolicyException.vue"),
    meta: { authRequired: true,
      permission: "Can create Policy Exceptions", title: "Request Exception / Waiver" },
  },
  {
    path: "/policies-sops/exceptions/:id/view",
    name: "policy.exceptions.view",
    component: () => import("@/views/policies/ViewPolicyException.vue"),
    meta: { authRequired: true,
      permission: "Can view Policy Exceptions", title: "View Exception / Waiver" },
  },
];
// Config & Control Centre (RCS-132) — one named route per governed
// namespace, one shared page (ConfigPage.vue reads route.meta.namespace,
// the same way LookupManager.vue reads route.meta.lookupType).
const configNamespaceRoutes = [
  {
    path: "/config/governance",
    name: "config.governance",
    meta: { title: setTitle("Configuration Governance"), authRequired: true, permission: "Can view Settings" },
    component: () => import("@/views/config/ConfigGovernanceView.vue"),
  },
  // Business Calendar gets its own page (year calendar + activities) instead
  // of the generic ConfigPage.vue — everything else stays schema-driven.
  ...CONFIG_NAMESPACES.filter((ns) => ns.key !== "business-calendar").map((ns) => ({
    path: ns.path,
    name: ns.routeName,
    meta: { title: setTitle(ns.label), authRequired: true, permission: "Can view Settings", namespace: ns.key },
    component: () => import("@/views/config/ConfigPage.vue"),
  })),
  ...(() => {
    const businessCalendar = CONFIG_NAMESPACES.find((ns) => ns.key === "business-calendar");
    return businessCalendar
      ? [{
          path: businessCalendar.path,
          name: businessCalendar.routeName,
          meta: { title: setTitle(businessCalendar.label), authRequired: true, permission: "Can view Settings", namespace: businessCalendar.key },
          component: () => import("@/views/config/BusinessCalendarView.vue"),
        }]
      : [];
  })(),
];

// My Work (RCS-134): one route per sidebar item, each a real, filtered page
// backed by the same shared component — see config/myWorkSources.ts.
const myWorkRoutes = MY_WORK_SOURCES.map((source) => ({
  path: source.path,
  name: source.routeName,
  meta: { title: setTitle(source.label), authRequired: true, permission: "Can view My Work", myWorkSource: source },
  component: () => import("@/views/my-work/MyWorkModuleList.vue"),
}));

// One route per not-yet-built target-menu capability, all sharing the same
// placeholder component — see src/config/stubPages.ts.
const stubPageRoutes = Object.values(STUB_PAGES).map((config) => ({
  path: `/coming-soon/${config.key.replace(/\./g, "/")}`,
  name: `stub.${config.key}`,
  meta: {
    title: setTitle(config.label),
    authRequired: true,
    stubKey: config.key,
  },
  component: () => import("@/views/shared/ComingSoonPage.vue"),
}));

export const allRoute = [
  ...fileRoutes,
  ...authRoutes,
  ...errorRoutes,
  ...dashboardRoutes,
  ...myWorkRoutes,
  ...complianceRoutes,
  ...RiskRoutes,
  ...incidentRoutes,
  ...investigationRoutes,
  ...lossEventRoutes,
  ...fraudRoutes,
  ...CapaRoutes,
  ...FindingRoutes,
  ...RcaRoutes,
  ...ControlRoutes,
  ...ControlMappingRoutes,
  ...ControlTestRoutes,
  ...ControlMonitoringRoutes,
  ...ControlExceptionRoutes,
  ...ControlEffectivenessRoutes,
  ...RegulationRoutes,
  ...PolicyUniverseRoutes,
  ...ObligationRoutes,
  ...ObligationControlMappingRoutes,
  ...PolicyHierarchyRoutes,
  ...RegulatoryChangeRoutes,
  ...GovernanceCalendarRoutes,
  ...ComplianceAssessmentRoutes,
  ...ExternalRegulatoryComplianceRoutes,
  ...InternalPolicyComplianceRoutes,
  ...ComplianceMonitoringRoutes,
  ...ComplianceCalendarRoutes,
  ...RegulatoryReturnRoutes,
  ...LicenceRoutes,
  ...RegulatoryExaminationRoutes,
  ...executiveDashboardRoutes,
  ...boardRoutes,
  ...systemRoutes,
  ...referenceDataRoutes,
  ...reportRoutes,
  ...escalationManagementRoutes,
  ...specialisedComplianceRoutes,
  ...AuditPlanRoutes,
  ...documentRoutes,
  ...evidenceRoutes,
  ...policySopRoutes,
  ...notificationRoutes,
  ...configNamespaceRoutes,
  ...stubPageRoutes,
];
